/**
 * React Query Invalidation Utilities
 *
 * This file provides reusable functions for invalidating and managing the query cache
 * in a consistent way across the application. These utilities help ensure that:
 *
 * 1. Data is properly refreshed after mutations
 * 2. Related entities are kept in sync
 * 3. We avoid over-fetching by targeting specific queries
 */import{useQueryClient}from'@tanstack/react-query';import{useCallback}from'react';/**
 * Creates a hook for invalidating queries related to a specific entity type
 *
 * @param queryKeyFactory - The query key factory for the entity
 * @returns A hook that provides invalidation functions
 */export const createEntityInvalidationHook=queryKeyFactory=>{// Type guard to ensure the factory has the expected structure
const keyFactory=queryKeyFactory;return scopeId=>{const queryClient=useQueryClient();/**
     * Invalidates all queries for this entity type
     */const invalidateAll=useCallback(()=>{return queryClient.invalidateQueries({queryKey:keyFactory.all});},[queryClient]);/**
     * Invalidates list queries for this entity type
     */const invalidateLists=useCallback(()=>{// If a lists function exists, use it; otherwise fall back to 'all'
const queryKey=keyFactory.lists?scopeId?keyFactory.lists(scopeId):keyFactory.lists():keyFactory.all;return queryClient.invalidateQueries({queryKey});},[queryClient,scopeId]);/**
     * Invalidates a specific entity by ID
     */const invalidateEntity=useCallback(entityId=>{if(!keyFactory.details){console.warn('No details query key defined for entity type');return Promise.resolve();}const queryKey=scopeId?keyFactory.details(entityId,scopeId):keyFactory.details(entityId);return queryClient.invalidateQueries({queryKey});},[queryClient,scopeId]);/**
     * Combined function to invalidate both list and a specific entity
     */const invalidateListAndEntity=useCallback(entityId=>{return Promise.all([invalidateLists(),invalidateEntity(entityId)]);},[invalidateLists,invalidateEntity]);return{invalidateAll,invalidateLists,invalidateEntity,invalidateListAndEntity};};};/**
 * Generic utility for invalidating and refetching lists
 * Use this when you need to ensure the UI updates immediately
 *
 * @param queryClient - The QueryClient instance
 * @param queryKey - The query key to invalidate
 * @param options - Options for invalidation
 */export const invalidateAndRefetch=async(queryClient,queryKey,options)=>{const{refetch=true}=options||{};// First invalidate to mark as stale
await queryClient.invalidateQueries({queryKey});// Optionally force a refetch to update UI immediately
if(refetch){await queryClient.refetchQueries({queryKey});}};/**
 * Utility hook for optimistic updates in mutations
 * This helps in updating the UI immediately before the server confirms
 *
 * @param queryClient - The QueryClient instance
 * @param queryKey - The query key to update
 * @param updateFn - Function that updates the cache data
 */export const optimisticUpdate=(queryClient,queryKey,updateFn)=>{// Store previous value for rollback
const previousData=queryClient.getQueryData(queryKey);// Update the cache optimistically
queryClient.setQueryData(queryKey,oldData=>updateFn(oldData));// Return rollback function for error cases
return()=>{queryClient.setQueryData(queryKey,previousData);};};/**
 * Hook that provides utilities for managing related entities
 * Use when changes to one entity type should invalidate related entities
 */export const useRelatedEntitiesInvalidation=()=>{const queryClient=useQueryClient();return useCallback(relationships=>{return Promise.all(relationships.map(_ref=>{let{key,refetch}=_ref;return invalidateAndRefetch(queryClient,key,{refetch});}));},[queryClient]);};